import { useChannelMutate } from '@/app/components/collections/hooks'
import HoverableTooltip from '@/app/components/hoverable-tooltip'
import LockIcon from '@/app/icons/lock'
import StarIcon from '@/app/icons/star'
import TrashIcon from '@/app/icons/trash'
import { editChannelJson, removeGifsFromChannel } from '@/app/util/channel-api'
import { GridContext } from '@/components/grid/grid'
import GifOverlay from '@/components/grid/overlay/gif-overlay'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { GifOverlayProps } from '@giphy/react-components'
import { useContext, useState } from 'react'

export function generateId(prefix?: string): string {
    return `${prefix}${Math.random().toString(36).substring(2, 11)}`
}

type Props = { channel: IChannel } & GifOverlayProps
const ChannelOverlay = ({ gif, isHovered, width, channel }: Props) => {
    const { setGifs } = useContext(GridContext)
    const { showMessage } = useContext(FlashMessageContext)
    const [isRemoving, setIsRemoving] = useState(false)
    const mutate = useChannelMutate()
    const { user } = useContext(UserContext)
    const canEdit = !!(user && (user.is_staff || user.username === channel.user.username))
    if (!canEdit) {
        return <GifOverlay gif={gif} isHovered={isHovered} />
    }
    const isCurrentlyFeatured = channel.featured_gif?.id === gif.id
    return (
        <>
            {gif.is_hidden && (
                <div className="pointer-events-none absolute inset-0 flex select-none items-center justify-center">
                    <LockIcon className="fill-giphyRed size-10" />
                </div>
            )}
            {isHovered && (
                <div className="bg-giphyBlack/10 absolute left-2.5 top-2.5 flex items-center gap-3 rounded-md p-2">
                    <HoverableTooltip label={'Remove from Channel'}>
                        <TrashIcon
                            className="hover:fill-giphyRed block size-5 cursor-pointer"
                            onClick={async (e) => {
                                setIsRemoving(true)
                                e.preventDefault()
                                e.stopPropagation()
                                function onFailed() {
                                    showMessage({ message: 'There was an error removing the gif', type: 'error' })
                                    setIsRemoving(false)
                                }
                                try {
                                    const result = await removeGifsFromChannel([gif.id], channel.id)
                                    if (!result.ok) {
                                        onFailed()
                                    } else {
                                        setGifs((gifs) => gifs.filter((g) => g.id !== gif.id))
                                    }
                                } catch (e) {
                                    onFailed()
                                }
                            }}
                        />
                    </HoverableTooltip>
                    <HoverableTooltip
                        label={isCurrentlyFeatured ? 'Click another GIF to change the cover GIF' : 'Make Cover GIF'}
                    >
                        <StarIcon
                            onClick={async (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                try {
                                    await editChannelJson(channel.id, {
                                        id: channel.id,
                                        parent: channel.parent,
                                        featured_gif: gif.id.toString(),
                                    })
                                    mutate({ ...channel, featured_gif: gif })
                                } catch (error) {
                                    showMessage({
                                        message: 'There was an error making the gif cover',
                                        type: 'error',
                                    })
                                }
                            }}
                            className={cn(
                                'hover:fill-giphyYellow block size-5 cursor-pointer',
                                isCurrentlyFeatured && 'fill-giphyRed hover:fill-giphyRed cursor-default'
                            )}
                        />
                    </HoverableTooltip>
                </div>
            )}
            <GifOverlay gif={gif} isHovered={isHovered} width={width}></GifOverlay>
            <div className={cn(isRemoving && 'bg-giphyBlack/40 pointer-events-none absolute inset-0')} />
        </>
    )
}

export default ChannelOverlay
